/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import useCurrentUser from '../customHook/useCurrentUser';
import { useLocation } from 'react-router-dom';

const sectionExplanations: Record<string, string> = {
  'App Drawer':
    '🔗 This is the App Drawer. Connect to tools like Slack, or google drive apps from here.',
  'AI Chatbot':
    '💬 Meet Tara, your AI assistant. Use it to get reports, dashboards, or help with daily tasks!',
  'Add My Team': '➕ Use this button to add new members to your team quickly.',
  Dashboard:
    '📊 This is your Dashboard — get an overview of key metrics, company updates, and quick insights at a glance.',
  Agent:
    '⏱ The Agent tab lets you mark your own attendance and log work hours quickly.',
  Docs: '📄 Access and manage company documents here — policies, HR forms, or any official files.',
  Calendar:
    '🗓 Check the Company Calendar for holidays, events, and special occasions.',

  Team: '👥 Manage team members — update profiles, roles, salaries, Slack IDs, and details.',
  Attendance: '⏰ Track and review employee attendance records here.',
  'Employee Logs': '📋 Detailed logs of when employees clocked in and out.',
  'Monthly View': '📆 Visual summary of attendance trends across the month.',
  Leaves: '🌴 Apply for leaves or check your team’s leave requests here.',
  'My Leaves': '🙋 Apply for your personal leaves and view your leave history.',
  'Leave Requests':
    '🧑‍🤝‍🧑 Review and approve pending leave requests from your team.',
  Offboarding:
    '🔄 Manage the offboarding process for employees leaving the company.',
  Manage: '⚙️ HR/Admin tools to configure and handle offboarding workflows.',
  'Initiate Offboarding': '🚪 Start a new offboarding process for an employee.',
  'Online Users':
    '🟢 See which team members are currently online and available.',
  Projects:
    '📁 Manage and monitor ongoing projects, deadlines, and deliverables.',
  Payslip: '💵 Access your payslips and salary history here.',
  Regularization: '🕒 Request corrections for attendance or missed clock-ins.',
  'Slack Activity': '💬 View team activity and collaboration trends here.',
  Grievances:
    '📝 Submit and track workplace concerns or grievances confidentially.',
  'Work Gallery':
    '🖼 The Work Gallery shows automatically captured screenshots of user activity while tracking work. Use it to review progress, accountability, and task focus.',
  'Download Report':
    '📥 Click here to download reports for your team or company data.',
};

const sectionSelectors: Record<string, string> = {
  'App Drawer': '.app-drawer',
  'AI Chatbot': '.ai-chatbot',
  Dashboard: '.sidebar-dashboard',
  Agent: '.sidebar-agent',
  Docs: '.sidebar-docs',
  Calendar: '.sidebar-calendar',
  Team: '.sidebar-team',
  Attendance: '.sidebar-attendance',
  'Employee Logs': '.sidebar-employee-logs',
  'Monthly View': '.sidebar-monthly-view',
  Leaves: '.sidebar-leaves',
  'My Leaves': '.sidebar-my-leaves',
  'Leave Requests': '.sidebar-leave-requests',
  Offboarding: '.sidebar-offboarding',
  Manage: '.sidebar-offboarding-manage',
  'Initiate Offboarding': '.sidebar-initiate-offboarding',
  'Online Users': '.sidebar-online-users',
  Projects: '.sidebar-projects',
  Payslip: '.sidebar-payslip',
  Regularization: '.sidebar-regularization',
  'Slack Activity': '.sidebar-slack-activity',
  Grievances: '.sidebar-grievances',
  'Work Gallery': '.sidebar-work-gallery',
  'Download Report': '.download-report-btn', // ← added selector
  'Add My Team': '.add-my-team-btn',
};

const GlobalIntroGuide = () => {
  const currentUser = useCurrentUser();
  const location = useLocation();

  const runIntro = () => {
    const readySteps = Object.entries(sectionSelectors)
      .filter(([key, selector]) => {
        const el = document.querySelector(selector);
        if (!el) {
          console.warn(
            `⚠️ Skipping missing element for section: ${key}, selector: ${selector}`
          );
          return false;
        }
        return true;
      })
      .map(([key, selector]) => ({
        element: selector,
        intro: sectionExplanations[key] || `📌 ${key}`,
      }));

    if (readySteps.length === 0) return;

    setTimeout(() => {
      introJs()
        .setOptions({
          steps: readySteps,
          showProgress: true,
          showBullets: false,
          exitOnOverlayClick: true,
          disableInteraction: false,
          overlayOpacity: 0.4,
          nextLabel: 'Next →',
          prevLabel: '← Back',
          doneLabel: 'Got it!',
          tooltipClass: 'custom-intro-tooltip',
        })
        .oncomplete(() => localStorage.setItem('hasAppIntroSeen', 'true'))
        .onexit(() => localStorage.setItem('hasAppIntroSeen', 'true'))
        .start();
    }, 1000);
  };

  useEffect(() => {
    const handleModalClosed = () => {
      const hasSeenIntro = localStorage.getItem('hasAppIntroSeen') === 'true';
      if (hasSeenIntro) return;
      runIntro();
    };

    window.addEventListener('taraModalClosed', handleModalClosed);
    return () => {
      window.removeEventListener('taraModalClosed', handleModalClosed);
    };
  }, []);

  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasAppIntroSeen') === 'true';
    if (hasSeenIntro) return;

    const hasSeenTaraModal =
      localStorage.getItem('hasSeenTaraModal') === 'true';

    if (currentUser?.role !== 'Employee' && !hasSeenTaraModal) {
      return;
    }
    const restrictedRoutes = ['/upload/docs', '/employee'];
    if (restrictedRoutes.includes(location.pathname)) {
      return;
    }

    runIntro();
  }, [currentUser?.role, location.pathname]);

  return null;
};

export default GlobalIntroGuide;
